import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

import type {
  MessengerIncomingEvent,
  MessengerScope,
} from "../../types/messengerEvents";
import { useMessengerSubscribe } from "../../app/messenger/MessengerSocketContext";

const AUTO_CLEAR_MS = 7000;
const MAX_VISIBLE_USERS = 3;
const UNKNOWN_USER_LABEL = "ユーザー";

/**
 * scope + key 単位で受信した typing.*.started / stopped を集約し、
 * 「◯◯ さんが入力中...」を表示するコンポーネント。
 *
 * - BE 仕様書「Messenger リアルタイム連携」§4.2 準拠。
 * - started 受信ごとに 7s auto-clear を更新。
 * - stopped 受信・再接続・unmount で即時クリア。
 * - parent_id は参照せず「このスコープで誰かが入力中」を集約表示する（計画の方針）。
 */
interface TypingIndicatorProps {
  scope: MessengerScope;
  /** スコープの primary id（project_id / shot_id / dm_id）。 */
  scopeKey: string | undefined;
  /** 現在のユーザー ID。自分自身の typing は表示しない（セルフエコー除外の安全弁）。 */
  currentUserId?: number;
  /**
   * user_id → 表示名。メンバー一覧から作成して渡す。
   * 見つからなかった場合は "ユーザー" と表示する。
   */
  resolveUserName?: (userId: number) => string | undefined;
}

const TypingIndicator: React.FC<TypingIndicatorProps> = ({
  scope,
  scopeKey,
  currentUserId,
  resolveUserName,
}) => {
  const [typingUserIds, setTypingUserIds] = useState<number[]>([]);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const clearTimer = useCallback((userId: number) => {
    const t = timersRef.current.get(userId);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(userId);
    }
  }, []);

  const addOrRefreshUser = useCallback(
    (userId: number) => {
      clearTimer(userId);
      const timer = setTimeout(() => {
        timersRef.current.delete(userId);
        setTypingUserIds((prev) => prev.filter((id) => id !== userId));
      }, AUTO_CLEAR_MS);
      timersRef.current.set(userId, timer);
      setTypingUserIds((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
    },
    [clearTimer],
  );

  const removeUser = useCallback(
    (userId: number) => {
      clearTimer(userId);
      setTypingUserIds((prev) => prev.filter((id) => id !== userId));
    },
    [clearTimer],
  );

  const handleEvent = useCallback(
    (ev: MessengerIncomingEvent) => {
      // typing.<scope>.started / stopped 以外は無視。
      if (!ev.type.startsWith("typing.")) return;
      const payload = ev.payload as { user_id?: number };
      const userId = payload?.user_id;
      if (typeof userId !== "number") return;
      if (currentUserId && userId === currentUserId) return;

      if (ev.type.endsWith(".started")) {
        addOrRefreshUser(userId);
      } else if (ev.type.endsWith(".stopped")) {
        removeUser(userId);
      }
    },
    [addOrRefreshUser, removeUser, currentUserId],
  );

  useMessengerSubscribe(scope, scopeKey, handleEvent);

  // scope / key が変わったら表示をリセット（過去スレッドの入力中を引きずらない）。
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
      setTypingUserIds([]);
    };
  }, [scope, scopeKey]);

  const label = useMemo(() => {
    if (typingUserIds.length === 0) return null;
    const names = typingUserIds.map(
      (id) => resolveUserName?.(id) ?? UNKNOWN_USER_LABEL,
    );
    // 多人数のときは最大件数までを列挙し、残りは「ほか N 名」に集約。
    if (names.length <= MAX_VISIBLE_USERS) {
      return `${names.join("、")} さんが入力中`;
    }
    const visible = names.slice(0, MAX_VISIBLE_USERS).join("、");
    return `${visible} ほか ${names.length - MAX_VISIBLE_USERS} 名が入力中`;
  }, [typingUserIds, resolveUserName]);

  if (!label) return null;

  return (
    <Wrapper aria-live="polite">
      <Dots aria-hidden="true">
        <span />
        <span />
        <span />
      </Dots>
      <Text>{label}</Text>
    </Wrapper>
  );
};

export default TypingIndicator;

// ─── styles ───────────────────────────────────────────

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40%           { opacity: 1; }
`;

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 4px;
  min-height: 18px;
  color: var(--soremo-light-gray);
  font-size: 12px;
  line-height: 1;
  user-select: none;
`;

const Dots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;

  span {
    display: inline-block;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: currentColor;
    animation: ${blink} 1.2s infinite ease-in-out both;
  }
  span:nth-child(1) {
    animation-delay: -0.32s;
  }
  span:nth-child(2) {
    animation-delay: -0.16s;
  }
`;

const Text = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
